import { useCallback, useEffect, useState } from "react";
import { hasRole } from "@/lib/roles";
import { Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import {
    MapPin, Plus, Trash2, Save, Search, Crosshair, X, Loader2, Building2,
} from "lucide-react";

const DEFAULT_RADIUS = 200;

export default function Worksites() {
    const { user } = useAuth();
    const { confirm } = useConfirm();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // worksite being edited or new
    const role = user?.role;
    const canManage = hasRole(user, "admin", "hr");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/worksites");
            setList(r.data || []);
        } catch (e) {
            toast.error("Failed to load worksites");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onDelete = async (w) => {
        const ok = await confirm({
            title: `Delete "${w.name}"?`,
            description: "Employees won't be able to check in at this worksite anymore. Existing attendance records are kept.",
            confirmLabel: "Delete worksite",
            danger: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/worksites/${w.id}`);
            toast.success("Worksite deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
        }
    };

    // Role gate — non-admin/HR users get bounced to the dashboard.
    // Sidebar already hides this link; this is defence-in-depth for direct URL access.
    if (user && !canManage) {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="worksites-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Geofenced Attendance</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        Worksites
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-xl">
                        Employees can only check in when they&apos;re within a worksite&apos;s radius. Add your offices, sites, and outposts here.
                    </p>
                </div>
                {canManage && (
                    <button
                        onClick={() => setEditing({})}
                        className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein-500"
                        data-testid="worksites-add-btn"
                    >
                        <Plus className="w-4 h-4" /> Add worksite
                    </button>
                )}
            </div>

            <div className="mt-10 bg-white border border-ink/10 overflow-auto">
                {loading ? (
                    <div className="py-16 text-center text-sm text-ink/40">
                        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
                    </div>
                ) : list.length === 0 ? (
                    <div className="py-16 text-center text-sm text-ink/40 border-dashed">
                        <Building2 className="w-8 h-8 mx-auto mb-3 text-ink/30" />
                        No worksites yet. {canManage && "Add one to enable employee check-in."}
                    </div>
                ) : (
                    <table className="w-full border-collapse text-sm">
                        <thead>
                            <tr className="border-b-2 border-ink/20">
                                <th className="text-left overline px-4 py-3">Name</th>
                                <th className="text-left overline px-4 py-3 hidden md:table-cell">Address</th>
                                <th className="text-left overline px-4 py-3">Coordinates</th>
                                <th className="text-right overline px-4 py-3">Radius</th>
                                <th className="text-left overline px-4 py-3">Status</th>
                                <th className="text-right overline px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((w) => (
                                <tr key={w.id} className="border-b border-ink/10 hover:bg-bone/40" data-testid={`worksite-row-${w.id}`}>
                                    <td className="px-4 py-3 font-medium text-ink">{w.name}</td>
                                    <td className="px-4 py-3 text-ink/60 hidden md:table-cell">{w.address || "—"}</td>
                                    <td className="px-4 py-3 font-mono text-xs text-ink/70">
                                        {w.latitude?.toFixed(5)}, {w.longitude?.toFixed(5)}
                                    </td>
                                    <td className="px-4 py-3 text-right tabular-nums">{w.radius_m}m</td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex items-center px-2 py-1 border text-[11px] uppercase tracking-[0.15em] ${
                                            w.is_active !== false ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-ink/5 text-ink/60 border-ink/15"
                                        }`}>
                                            {w.is_active !== false ? "Active" : "Disabled"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        {canManage && (
                                            <div className="flex items-center justify-end gap-2">
                                                <button onClick={() => setEditing(w)} data-testid={`worksite-edit-${w.id}`}
                                                    className="px-3 py-1.5 border border-ink/15 text-xs hover:bg-ink/5">
                                                    Edit
                                                </button>
                                                <button onClick={() => onDelete(w)} data-testid={`worksite-delete-${w.id}`}
                                                    className="p-1.5 border border-red-300 text-red-700 hover:bg-red-50">
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {editing && (
                <WorksiteEditor
                    worksite={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

function WorksiteEditor({ worksite, onClose, onSaved }) {
    const isNew = !worksite.id;
    const [name, setName] = useState(worksite.name || "");
    const [address, setAddress] = useState(worksite.address || "");
    const [lat, setLat] = useState(worksite.latitude != null ? String(worksite.latitude) : "");
    const [lng, setLng] = useState(worksite.longitude != null ? String(worksite.longitude) : "");
    const [radius, setRadius] = useState(worksite.radius_m || DEFAULT_RADIUS);
    const [isActive, setIsActive] = useState(worksite.is_active !== false);
    const [saving, setSaving] = useState(false);
    const [geocoding, setGeocoding] = useState(false);
    const [geocodeQ, setGeocodeQ] = useState("");
    const [results, setResults] = useState([]);
    const [locating, setLocating] = useState(false);

    const onGeocode = async () => {
        const q = (geocodeQ || address).trim();
        if (q.length < 3) {
            toast.error("Enter at least 3 characters to search");
            return;
        }
        setGeocoding(true); setResults([]);
        try {
            const r = await api.get("/geocode", { params: { q } });
            setResults(r.data.results || []);
            if (!r.data.results?.length) toast.message("No matches — try a more specific address.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Geocoding unavailable. Enter coordinates manually.");
        } finally {
            setGeocoding(false);
        }
    };

    const onUseLocation = async () => {
        if (!("geolocation" in navigator)) {
            toast.error("Geolocation not supported on this device");
            return;
        }
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            (p) => {
                setLat(p.coords.latitude.toFixed(6));
                setLng(p.coords.longitude.toFixed(6));
                toast.success(`Coordinates set from your device · ±${Math.round(p.coords.accuracy)}m`);
                setLocating(false);
            },
            () => { toast.error("Couldn't get your location. Allow permission and try again."); setLocating(false); },
            { enableHighAccuracy: true, timeout: 15000 }
        );
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            name: name.trim(),
            latitude: parseFloat(lat),
            longitude: parseFloat(lng),
            radius_m: parseInt(radius, 10) || DEFAULT_RADIUS,
            address: address.trim(),
            is_active: !!isActive,
        };
        if (!payload.name) { toast.error("Name is required"); return; }
        if (isNaN(payload.latitude) || isNaN(payload.longitude)) { toast.error("Latitude and longitude are required"); return; }
        setSaving(true);
        try {
            if (isNew) await api.post("/worksites", payload);
            else await api.put(`/worksites/${worksite.id}`, payload);
            toast.success(isNew ? "Worksite created" : "Worksite saved");
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="worksite-editor">
            <form onSubmit={onSubmit} className="bg-white max-w-2xl w-full max-h-[90vh] overflow-auto">
                <div className="flex items-center justify-between p-6 border-b border-ink/10">
                    <div>
                        <div className="overline">{isNew ? "New" : "Editing"}</div>
                        <h2 className="font-display text-2xl tracking-tighter mt-1">{isNew ? "Add worksite" : worksite.name}</h2>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 hover:bg-ink/5" data-testid="worksite-editor-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    <div>
                        <label className="overline">Name *</label>
                        <input
                            type="text" value={name} onChange={(e) => setName(e.target.value)} required
                            data-testid="worksite-name"
                            className="mt-2 w-full border border-ink/20 px-3 py-2.5 text-sm focus:border-klein focus:outline-none"
                            placeholder="e.g. HQ Lagos, Abuja Warehouse"
                        />
                    </div>

                    <div>
                        <label className="overline flex items-center justify-between">
                            <span>Address / search</span>
                            <span className="text-[10px] text-ink/40 normal-case tracking-normal">Powered by OpenStreetMap</span>
                        </label>
                        <div className="mt-2 flex gap-2">
                            <input
                                type="text" value={geocodeQ || address} onChange={(e) => { setGeocodeQ(e.target.value); setAddress(e.target.value); }}
                                data-testid="worksite-address"
                                className="flex-1 border border-ink/20 px-3 py-2.5 text-sm focus:border-klein focus:outline-none"
                                placeholder="Type an address and click Search…"
                            />
                            <button
                                type="button" onClick={onGeocode} disabled={geocoding}
                                data-testid="worksite-geocode-btn"
                                className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink/5 disabled:opacity-50"
                            >
                                {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                                Search
                            </button>
                        </div>
                        {results.length > 0 && (
                            <ul className="mt-2 border border-ink/10 max-h-44 overflow-auto text-sm bg-bone/40" data-testid="worksite-geocode-results">
                                {results.map((r, i) => (
                                    <li key={i}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setLat(r.latitude.toFixed(6));
                                                setLng(r.longitude.toFixed(6));
                                                setAddress(r.display_name);
                                                setGeocodeQ(r.display_name);
                                                setResults([]);
                                                toast.success("Coordinates set");
                                            }}
                                            className="w-full text-left px-3 py-2 hover:bg-white border-b border-ink/5"
                                            data-testid={`worksite-geocode-result-${i}`}
                                        >
                                            <div className="text-ink line-clamp-1">{r.display_name}</div>
                                            <div className="text-xs text-ink/40 font-mono">{r.latitude.toFixed(5)}, {r.longitude.toFixed(5)}</div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="overline">Latitude *</label>
                            <input
                                type="number" step="0.0000001" value={lat} onChange={(e) => setLat(e.target.value)} required
                                data-testid="worksite-latitude"
                                className="mt-2 w-full border border-ink/20 px-3 py-2.5 text-sm font-mono focus:border-klein focus:outline-none"
                                placeholder="6.5244"
                            />
                        </div>
                        <div>
                            <label className="overline">Longitude *</label>
                            <input
                                type="number" step="0.0000001" value={lng} onChange={(e) => setLng(e.target.value)} required
                                data-testid="worksite-longitude"
                                className="mt-2 w-full border border-ink/20 px-3 py-2.5 text-sm font-mono focus:border-klein focus:outline-none"
                                placeholder="3.3792"
                            />
                        </div>
                    </div>

                    <button
                        type="button" onClick={onUseLocation} disabled={locating}
                        data-testid="worksite-use-current-location"
                        className="inline-flex items-center gap-2 border border-emerald-500 text-emerald-700 px-4 py-2 text-sm hover:bg-emerald-50 disabled:opacity-50"
                    >
                        {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />}
                        Use my current location
                    </button>

                    <div className="grid grid-cols-2 gap-3 items-end">
                        <div>
                            <label className="overline">Geofence radius (m)</label>
                            <input
                                type="number" min={20} max={5000} value={radius} onChange={(e) => setRadius(e.target.value)}
                                data-testid="worksite-radius"
                                className="mt-2 w-full border border-ink/20 px-3 py-2.5 text-sm focus:border-klein focus:outline-none"
                            />
                            <p className="text-[11px] text-ink/40 mt-1">Min 20m, max 5,000m. Default 200m works for most offices.</p>
                        </div>
                        <label className="flex items-center gap-2 mb-2 text-sm" data-testid="worksite-active-toggle">
                            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                            <span>Active (employees can check in here)</span>
                        </label>
                    </div>

                    {lat && lng && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng)) && (
                        <div className="border border-ink/10 overflow-hidden" data-testid="worksite-map-preview">
                            <div className="overline px-3 pt-2 pb-1">Preview</div>
                            <iframe
                                title="map preview"
                                width="100%" height="280" frameBorder="0" scrolling="no"
                                src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(lng) - 0.005}%2C${parseFloat(lat) - 0.003}%2C${parseFloat(lng) + 0.005}%2C${parseFloat(lat) + 0.003}&layer=mapnik&marker=${lat}%2C${lng}`}
                            />
                            <div className="px-3 py-1.5 text-[11px] text-ink/40">
                                Pin at {parseFloat(lat).toFixed(5)}, {parseFloat(lng).toFixed(5)}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-3 p-6 border-t border-ink/10 bg-bone/30">
                    <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm hover:bg-ink/5" data-testid="worksite-editor-cancel">
                        Cancel
                    </button>
                    <button
                        type="submit" disabled={saving}
                        data-testid="worksite-editor-save"
                        className="inline-flex items-center gap-2 bg-klein text-white px-5 py-2.5 text-sm hover:bg-klein-500 disabled:opacity-60"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {isNew ? "Create worksite" : "Save changes"}
                    </button>
                </div>
            </form>
        </div>
    );
}
